import * as React from 'react';
import { cn } from '@/lib/utils/cn';
import { uploadFileChunked } from '@/lib/api/documents';
import { setEngagementBanner } from '@/lib/api/engagements';
import { useAuthedImageUrl } from '@/lib/api/authed-image';

/**
 * ImageSlot — React port of `design_handoff_caliber_v2/prototype/image-slot.js`.
 *
 * Why this is its own primitive:
 *   - The engagement-overview hero (T-0097) needs a drag-drop image
 *     target with a baked-in dark-gradient overlay so the hero title
 *     stays legible regardless of whether a photo has been dropped.
 *   - README §7 calls this out explicitly: the gradient is part of the
 *     component, NOT a sibling element. Children (the hero title) render
 *     above the gradient. Future ticket consumers (T-0097) drop the
 *     entire title strip in as `children` and inherit legibility for free.
 *
 * What changes vs. the prototype Custom Element:
 *   - Persistence: the prototype writes to a `.image-slots.state.json`
 *     sidecar via the omelette runtime. This React port REPLACES that
 *     with the real T-0091 path:
 *       1. The dropped file is uploaded via the T-0040 chunked-upload
 *          flow (`uploadFileChunked` in `lib/api/documents.ts`) — POST
 *          /api/v1/documents → PUT chunks → POST complete.
 *       2. The resulting `document_id` is POSTed to
 *          /api/v1/engagements/:id/banner (the T-0091 endpoint), which
 *          stores `projects.banner_image_url`.
 *       3. The endpoint returns the new EngagementDTO; we read
 *          `banner_image_url` and use it for the visible image. It is an
 *          API path (the storage account allows no public blob access), so
 *          the bytes are fetched with the session's credentials
 *          (`useAuthedImageUrl`) and shown from an object URL.
 *   - No reframe / pan / zoom. The hero is a fixed crop driven by CSS
 *     `object-cover`; if a future ticket needs reframing, it can extend
 *     this component or fork a separate `ReframableImage` primitive.
 *   - No `localStorage` fallback (deliberately removed — the prototype's
 *     offline-demo approach has no place in production).
 *
 * What stays from the prototype:
 *   - The drag-drop affordance shape (full-bleed target, dashed ring on
 *     hover, "Drop … here" empty state, optional click-to-browse).
 *   - The gradient overlay (`linear-gradient(to bottom, transparent 0%,
 *     rgba(24,46,88,0.6) 100%)`) — Avero navy at 60% opacity over the
 *     image's bottom half. Tokens cannot capture multi-stop gradients
 *     cleanly, so the rule lives inline on the overlay div per the
 *     ticket's §6 risks-and-unknowns note ("keep it in the component's
 *     own scoped CSS so it travels with the component").
 *   - Drag-depth counter so dragging over child elements doesn't flicker
 *     the over-state on/off.
 *
 * Accessibility:
 *   - The dropzone is a `role="button"` element with the ticket-specified
 *     `aria-label="Engagement banner photo. Drag and drop an image here."`.
 *   - Enter / Space activate the hidden file input (browse fallback).
 *   - An "uploading" status is announced via `role="status"` while a drop
 *     is in flight.
 */

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;
type AcceptedMime = (typeof ACCEPTED_MIME_TYPES)[number];

function isAcceptedMime(mime: string): mime is AcceptedMime {
  return (ACCEPTED_MIME_TYPES as readonly string[]).includes(mime);
}

/**
 * Avero-navy gradient. Lives as an inline style because Tailwind cannot
 * express a multi-stop gradient with a custom color stop on the
 * Avero-navy token in a single utility class. The hex literal (#182E58)
 * mirrors `--avero-navy` — the navy token IS the source of truth, this
 * value is paired with it and updated together. Search:
 *   apps/web/src/globals.css → --avero-navy
 *   design_handoff_caliber_v2/README.md §5 → "Avero navy #182E58"
 */
const GRADIENT_OVERLAY_STYLE: React.CSSProperties = {
  backgroundImage: 'linear-gradient(to bottom, transparent 0%, rgba(24, 46, 88, 0.6) 100%)',
};

export interface ImageSlotProps {
  /** Engagement id (NOT project id) — used to POST the banner attach. */
  engagementId: string;
  /**
   * Project id — required by the chunked-upload initiate body. The
   * consumer (T-0097 hero) resolves this from `useProjectQuery` or the
   * route loader. Optional so a designer-only showcase can render the
   * slot without wiring an upload path; when omitted, drops are
   * rejected with a transient error.
   */
  projectId?: string;
  /**
   * Initial banner URL (the engagement's `banner_image_url` field from
   * the EngagementDTO). When `null` / `undefined` the empty state shows
   * the placeholder caption.
   */
  defaultImageUrl?: string | null;
  /**
   * Called after a successful upload + banner attach. Receives the
   * server-resolved banner URL so the parent can update its local
   * EngagementDTO cache without refetching.
   */
  onUploadComplete?: (url: string) => void;
  /**
   * Placeholder caption shown in the empty state. Defaults to the
   * prototype's wording.
   */
  placeholder?: string;
  /**
   * Hero overlay content. Anything passed here renders ABOVE the
   * gradient (z-10). Typically the city pill + posture pill + serif
   * title + facts strip from the hero pattern in README §7.
   */
  children?: React.ReactNode;
  /** Extra classes for the outer wrapper. */
  className?: string;
}

type DropState =
  | { kind: 'idle' }
  | { kind: 'over' }
  | { kind: 'uploading' }
  | { kind: 'error'; message: string };

export function ImageSlot({
  engagementId,
  projectId,
  defaultImageUrl,
  onUploadComplete,
  placeholder = 'Drop city/skyline photo here',
  children,
  className,
}: ImageSlotProps) {
  // The displayed image — starts at the engagement's saved banner and
  // updates optimistically when a drop succeeds. We do NOT mirror the
  // store-driven re-fetch here; the consumer that owns engagement state
  // (T-0097) controls invalidation. The `onUploadComplete` callback is
  // how the consumer learns the new URL.
  const [imageUrl, setImageUrl] = React.useState<string | null>(defaultImageUrl ?? null);
  // What the <img> shows: null while an API-served banner loads.
  const displayUrl = useAuthedImageUrl(imageUrl);
  const [drop, setDrop] = React.useState<DropState>({ kind: 'idle' });
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // Drag-enter / drag-leave fire for every descendant crossing — count
  // depth so hovering the icon inside the empty state doesn't flicker.
  const dragDepthRef = React.useRef(0);
  // Bump on each drop so a stale in-flight upload can't override a newer
  // one if the user drags a second file before the first finishes.
  const generationRef = React.useRef(0);

  // Mirror externally-driven default updates so a parent that re-fetches
  // the engagement after a cache invalidation can push a new URL in. The
  // local optimistic value is preserved if the parent re-passes the same
  // value (no-op).
  React.useEffect(() => {
    setImageUrl(defaultImageUrl ?? null);
  }, [defaultImageUrl]);

  // Transient error chip auto-dismisses after 3s — matches the prototype.
  React.useEffect(() => {
    if (drop.kind !== 'error') return;
    const t = window.setTimeout(() => {
      setDrop((s) => (s.kind === 'error' ? { kind: 'idle' } : s));
    }, 3000);
    return () => window.clearTimeout(t);
  }, [drop]);

  async function ingest(file: File | undefined | null) {
    if (!file) return;
    if (!isAcceptedMime(file.type)) {
      setDrop({ kind: 'error', message: 'Drop a PNG, JPEG, WebP, or GIF image.' });
      return;
    }
    if (!projectId) {
      setDrop({
        kind: 'error',
        message: 'Upload is not wired yet — projectId missing.',
      });
      return;
    }
    const gen = ++generationRef.current;
    setDrop({ kind: 'uploading' });
    try {
      const { document_id } = await uploadFileChunked(file, {
        projectId,
        title: file.name,
        sourceParty: 'avero',
      });
      // A second drop while the first is in flight wins — abandon this
      // result quietly so the user sees the newer state.
      if (gen !== generationRef.current) return;
      const dto = await setEngagementBanner(engagementId, document_id);
      if (gen !== generationRef.current) return;
      const url = dto.banner_image_url;
      if (!url) {
        setDrop({ kind: 'error', message: 'Server did not return a banner URL.' });
        return;
      }
      setImageUrl(url);
      setDrop({ kind: 'idle' });
      onUploadComplete?.(url);
    } catch (err) {
      if (gen !== generationRef.current) return;
      const message = err instanceof Error ? err.message : 'Upload failed.';
      setDrop({ kind: 'error', message });
    }
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  // Drag handlers — all four wired on the wrapper so the depth counter
  // accounts for every dragenter/dragleave on descendants. The slot
  // accepts drops anywhere inside its bounds, including overlapping
  // children.
  function onDragEnter(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    dragDepthRef.current += 1;
    setDrop((s) => (s.kind === 'uploading' ? s : { kind: 'over' }));
  }
  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    // Without preventDefault the browser never fires 'drop'.
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }
  function onDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDrop((s) => (s.kind === 'over' ? { kind: 'idle' } : s));
    }
  }
  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    const f = e.dataTransfer?.files?.[0];
    void ingest(f);
  }

  const isOver = drop.kind === 'over';
  const isUploading = drop.kind === 'uploading';
  const isFilled = imageUrl !== null;

  return (
    <div
      // The dropzone shape — the consumer controls width/height via the
      // outer className. The hero (T-0097) sizes this to ~280px tall;
      // the showcase demo uses 200px.
      role="button"
      tabIndex={0}
      aria-label="Engagement banner photo. Drag and drop an image here."
      aria-busy={isUploading}
      data-state={drop.kind}
      data-filled={isFilled ? '' : undefined}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={openFilePicker}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openFilePicker();
        }
      }}
      className={cn(
        'relative isolate overflow-hidden rounded-card bg-ink-100 cursor-pointer',
        'transition-colors',
        isOver && 'outline outline-2 outline-accent-amber-600 bg-accent-amber-50',
        className,
      )}
    >
      {/* Background image layer (z-0). object-cover keeps the fixed
          aspect ratio without distortion; alt="" because the photo is
          decorative — the hero title gives the accessible name. */}
      {displayUrl ? (
        <img
          src={displayUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 z-0 h-full w-full object-cover"
          draggable={false}
        />
      ) : null}

      {/* Gradient overlay (z-[1]) — baked in, always present so child
          text stays legible whether or not a photo is set. pointer-events-
          none so clicks pass through to the underlying button surface. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-[1] pointer-events-none"
        style={GRADIENT_OVERLAY_STYLE}
      />

      {/* Empty-state placeholder (z-[2]) — visible only when no photo
          and not over-state. Uses ink-700 text on ink-100 bg for the
          deliberately-flat look in the prototype. */}
      {!isFilled && !isOver && !isUploading ? (
        <div className="relative z-[2] flex h-full min-h-[120px] w-full flex-col items-center justify-center gap-1 px-4 text-center text-ink-500">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="m21 15-5-5L5 21" />
          </svg>
          <span className="text-body-sm font-medium text-ink-700">{placeholder}</span>
          <span className="text-meta text-ink-500">
            or <span className="underline underline-offset-2">browse files</span>
          </span>
        </div>
      ) : null}

      {/* Over-state hint — replaces the placeholder while a file is
          being dragged over. */}
      {isOver ? (
        <div className="relative z-[2] flex h-full min-h-[120px] w-full items-center justify-center text-body-sm font-medium text-accent-amber-700">
          Drop to upload
        </div>
      ) : null}

      {/* Uploading hint — anchors to the top-right so a hero title
          underneath stays readable. role=status announces progress. */}
      {isUploading ? (
        <div
          role="status"
          aria-live="polite"
          className="absolute right-3 top-3 z-[3] rounded-chip bg-ink-900/70 px-2 py-1 text-meta font-medium text-paper-0"
        >
          Uploading…
        </div>
      ) : null}

      {/* Transient error chip — auto-dismisses via the effect above. */}
      {drop.kind === 'error' ? (
        <div
          role="alert"
          className="absolute right-3 top-3 z-[3] rounded-chip bg-posture-critical px-2 py-1 text-meta font-medium text-paper-0"
        >
          {drop.message}
        </div>
      ) : null}

      {/* Hero overlay slot (z-[5]) — children render above the gradient
          so their content (serif title, pills, buttons) stays legible. */}
      {children ? (
        <div
          className="relative z-[5] flex h-full min-h-[120px] w-full flex-col justify-end p-6"
          // pointer-events-none on the wrapper, then re-enabled on the
          // children themselves — keeps the dropzone clickable on areas
          // BETWEEN child elements while children (buttons, links) work.
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      ) : null}

      {/* Hidden file input. Accept list mirrors ACCEPTED_MIME_TYPES so
          the OS picker only offers compatible files. */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MIME_TYPES.join(',')}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          void ingest(f);
          // Reset so re-picking the same file still triggers onChange.
          e.target.value = '';
        }}
      />
    </div>
  );
}

ImageSlot.displayName = 'ImageSlot';
